<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div class="d-flex align-items-center gap-3">
                <router-link :to="{ name: 'Production In Progress' }" class="btn btn-outline-secondary btn-sm">
                  <i class="fas fa-arrow-left me-1"></i> Volver
                </router-link>
                <h6 class="mb-0"><i class="fas fa-industry me-1" style="color: #2e389b;"></i> Producción #{{ orderId }}</h6>
              </div>
            </div>

            <div v-if="loading" class="text-center py-5">
              <i class="fas fa-spinner fa-spin fa-2x" style="color: #2e389b;"></i>
              <p class="mt-2 text-sm text-secondary">Cargando producción...</p>
            </div>

            <div v-else-if="order" class="mb-2">
              <div class="row">
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Creada por</label>
                  <p class="text-sm font-weight-bold mb-0">{{ order.creator?.name || '—' }}</p>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Fecha</label>
                  <p class="text-sm mb-0">{{ formatDate(order.created_at) }}</p>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Estado</label><br>
                  <select
                    :value="order.status || 'in_progress'"
                    class="status-select"
                    :class="orderStatusPillClass(order.status)"
                    :disabled="statusUpdating"
                    title="Cambiar estado de la producción"
                    @change="onOrderStatusChange($event)"
                  >
                    <option value="in_progress">En proceso</option>
                    <option value="fabricado">Fabricado</option>
                    <option value="terminado">Terminado</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Ventas relacionadas</label><br>
                  <div class="d-flex flex-wrap gap-1">
                    <router-link
                      v-for="remission in order.remissions"
                      :key="remission.id"
                      :to="{ name: 'Production Order', query: { id: remission.id } }"
                      class="badge badge-sm text-decoration-none"
                      style="background-color: #2e389b;"
                      :title="`Ver producción de la venta #${remission.id} — ${remission.name || ''}`"
                    >
                      Venta #{{ remission.id }}
                    </router-link>
                  </div>
                </div>
              </div>
              <div class="d-flex gap-2 flex-wrap mt-3">
                <span class="badge bg-secondary">Pares: {{ order.pairs || 0 }}</span>
                <span class="badge bg-info text-dark">Productos: {{ details.length }}</span>
                <span class="badge" :class="completedCount === details.length && details.length > 0 ? 'bg-success' : 'bg-warning text-dark'">
                  Fabricados: {{ completedCount }}/{{ details.length }}
                </span>
              </div>
            </div>
          </div>

          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="!loading && order" class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Producto</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ref</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Talla</th>
                    <th class="text-center text-secondary text-xxs font-weight-bolder opacity-7">Color</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Código de barras</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cant / Stock</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Orden</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in details" :key="item.id">
                    <td class="align-middle">
                      <h6 class="mb-0 text-sm">{{ item.inventory?.name || 'Producto' }}</h6>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ item.inventory?.ref || '—' }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ itemSize(item) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ itemColor(item) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ itemBarcode(item) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-sm font-weight-bold">{{ item.quantity }}</span> /
                      <span class="badge badge-sm" :class="itemStock(item) <= 0 ? 'bg-danger' : (itemStock(item) < item.quantity ? 'bg-warning text-dark' : 'bg-success')">
                        {{ itemStock(item) }}
                      </span>
                    </td>
                    <td class="text-center align-middle">
                      <router-link
                        :to="{ name: 'Production Order', query: { id: item.remission_id } }"
                        class="badge badge-sm text-decoration-none"
                        style="background-color: #2e389b;"
                        :title="`Ver producción de la venta #${item.remission_id}`"
                      >
                        #{{ item.remission_id }}
                      </router-link>
                    </td>
                    <td class="text-center align-middle">
                      <span v-if="itemManufacturingStatus(item) === 'completed'" class="status-badge status-badge--completed">
                        <i class="fas fa-check-circle"></i> Fabricado
                      </span>
                      <select
                        v-else
                        :value="itemManufacturingStatus(item)"
                        class="status-select"
                        :class="statusPillClass(item)"
                        @change="onStatusChange($event, item)"
                      >
                        <option value="pending">Pendiente</option>
                        <option value="in_progress">En proceso</option>
                        <option value="completed">Fabricado</option>
                      </select>
                    </td>
                  </tr>

                  <tr v-if="details.length === 0">
                    <td colspan="8" class="text-center text-xxs">Esta producción no tiene productos en proceso</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Oficios de la producción -->
          <div v-if="!loading && order" class="card-body px-4 pt-0 pb-4">
            <hr class="my-3">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h6 class="mb-0"><i class="fas fa-tools me-1" style="color: #2e389b;"></i> Oficios de la producción</h6>
              <span class="text-xs text-secondary">Asigne un usuario por oficio y registre fechas y cantidad terminada</span>
            </div>

            <div class="row g-3">
              <div v-for="oficio in oficios" :key="oficio.id" class="col-md-4 col-sm-6">
                <div class="card h-100 oficio-card">
                  <div class="card-body p-3">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <h6 class="mb-0 text-sm font-weight-bold">
                        <i class="fas fa-toolbox me-1" style="color: #2e389b;"></i> {{ oficio.name }}
                      </h6>
                      <span
                        class="badge badge-sm"
                        :class="oficioPendingDetails(oficio.id).length > 0 ? 'bg-warning text-dark' : 'bg-success'"
                        :title="`Registros sin asignar para este oficio`"
                      >
                        Pendientes: {{ oficioPendingDetails(oficio.id).length }}
                      </span>
                    </div>

                    <div v-if="oficioAssignments(oficio.id).length === 0" class="text-xs text-secondary mb-2">
                      Sin usuarios asignados
                    </div>
                    <ul v-else class="list-unstyled mb-2 oficio-users">
                      <li v-for="asg in oficioAssignments(oficio.id)" :key="asg.id" class="mb-2 border rounded p-2">
                        <div class="d-flex justify-content-between align-items-center">
                          <span class="text-xs font-weight-bold">{{ asg.user?.name || '—' }}</span>
                          <span :class="['badge badge-sm', assignmentBadgeClass(asg)]">
                            {{ assignmentStatusLabel(asg) }}
                          </span>
                        </div>
                        <div class="text-xxs text-secondary mt-1">
                          <i class="fas fa-calendar-alt me-1"></i> Inicio: {{ formatDate(asg.assigned_at) }}
                          <br><i class="fas fa-calendar-times me-1"></i> Fin (plazo): {{ formatDate(asg.estimated_at) }}
                        </div>

                        <!-- Seguimiento por producto -->
                        <template v-if="asg.details && asg.details.length > 0">
                          <div class="text-xxs text-secondary mt-1">
                            <i class="fas fa-shoe-prints me-1"></i> Terminados: <b>{{ assignmentDeliveredPairs(asg) }}/{{ assignmentTotalPairs(asg) }}</b> pares
                            · Gana: <b class="text-success">${{ assignmentEarned(asg).toLocaleString('es-CO') }}</b> / ${{ Number(asg.total || 0).toLocaleString('es-CO') }}
                          </div>
                          <div class="progress progress-xs mt-1" style="height: 4px;">
                            <div class="progress-bar bg-success" :style="{ width: (assignmentTotalPairs(asg) > 0 ? assignmentDeliveredPairs(asg) / assignmentTotalPairs(asg) * 100 : 0) + '%' }"></div>
                          </div>
                          <ul class="list-unstyled mt-2 mb-0 assignment-products">
                            <li v-for="asgDetail in asg.details" :key="asgDetail.id" class="d-flex align-items-center gap-2 py-1">
                              <input
                                type="checkbox"
                                class="form-check-input mt-0"
                                style="width: 14px; height: 14px; cursor: pointer;"
                                :checked="asgDetail.status === 'completed'"
                                :disabled="detailToggling === asgDetail.id"
                                :title="asgDetail.status === 'completed' ? 'Terminado — clic para desmarcar' : 'Marcar como terminado'"
                                @change="toggleAssignmentDetail(asg, asgDetail)"
                              />
                              <span class="flex-grow-1 text-xxs" :class="{ 'text-decoration-line-through text-secondary': asgDetail.status === 'completed' }">
                                <b>{{ asgDetail.remission_detail?.inventory?.name || 'Producto' }}</b>
                                <span class="text-secondary">{{ detailVariantLabel(asgDetail) }}</span>
                              </span>
                              <span class="text-xxs text-secondary text-end">
                                {{ asgDetail.delivered_quantity ?? 0 }}/{{ asgDetail.quantity }} × ${{ Number(asgDetail.price || 0).toLocaleString('es-CO') }}
                                <b :class="asgDetail.status === 'completed' ? 'text-success' : 'text-muted'">${{ ((asgDetail.delivered_quantity ?? 0) * Number(asgDetail.price || 0)).toLocaleString('es-CO') }}</b>
                              </span>
                            </li>
                          </ul>
                          <div v-if="asg.status === 'completed'" class="text-xxs text-secondary mt-1">
                            <i class="fas fa-calendar-check me-1"></i> Terminado: {{ formatDate(asg.completed_at) }}
                          </div>
                        </template>
                        <!-- Asignaciones antiguas sin detalle por producto -->
                        <div v-else class="text-xxs text-secondary mt-1">
                          <i class="fas fa-coins me-1"></i> Valor tarea: ${{ Number(asg.price || 0).toLocaleString('es-CO') }} × {{ asg.quantity }} =
                          <b class="text-success">${{ Number(asg.total || 0).toLocaleString('es-CO') }}</b>
                          <template v-if="asg.status === 'completed'">
                            <br><i class="fas fa-calendar-check me-1"></i> Terminado: {{ formatDate(asg.completed_at) }}
                            · Cantidad terminada: <b>{{ asg.delivered_quantity }}</b>
                          </template>
                        </div>

                        <div class="d-flex align-items-center gap-2 mt-1 assignment-actions">
                          <button
                            v-if="asg.status !== 'completed' && asg.details && asg.details.length > 0"
                            type="button"
                            class="btn btn-sm btn-success mb-0 py-1 px-2"
                            title="Marcar los productos terminados"
                            @click="openCompleteModal(asg)"
                          >
                            <i class="fas fa-check me-1"></i> Terminados
                          </button>
                          <button
                            v-if="asg.status !== 'completed' && (!asg.details || asg.details.length === 0)"
                            type="button"
                            class="btn btn-sm btn-success mb-0 py-1 px-2"
                            title="Marcar este oficio como terminado"
                            @click="openCompleteModal(asg)"
                          >
                            <i class="fas fa-check me-1"></i> Terminado
                          </button>
                          <span class="d-flex align-items-center gap-2 ms-auto">
                            <button type="button" class="btn btn-link btn-sm p-0 text-secondary" title="Editar asignación" @click="openAssignModal(oficio, asg)">
                              <i class="fas fa-pencil-alt"></i>
                            </button>
                            <button type="button" class="btn btn-link btn-sm p-0 text-danger" title="Eliminar asignación" @click="deleteAssignment(asg)">
                              <i class="fas fa-trash"></i>
                            </button>
                          </span>
                        </div>
                      </li>
                    </ul>

                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary w-100 mb-0"
                      :title="oficioUsers(oficio.id).length === 0 ? 'No hay usuarios habilitados para este oficio' : `Asignar usuario al oficio ${oficio.name}`"
                      @click="openAssignModal(oficio, null)"
                    >
                      <i class="fas fa-user-plus me-1"></i> Asignar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <p v-if="oficios.length === 0" class="text-xs text-secondary mb-0">No hay oficios configurados.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Asignar oficio -->
    <div v-if="showAssignModal" class="custom-modal-backdrop" @click.self="closeAssignModal">
      <div class="custom-modal custom-modal--wide">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">
            <i class="fas fa-user-plus me-1" style="color: #2e389b;"></i>
            {{ editingId ? 'Editar asignación' : 'Asignar' }} — {{ modalOficio?.name }}
          </h6>
          <button type="button" class="btn btn-link p-0 text-secondary" @click="closeAssignModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div v-if="modalError" class="alert alert-danger text-xs py-2 px-3 mb-3">{{ modalError }}</div>

        <div class="mb-3">
          <label class="form-control-label text-xs">Usuario habilitado para el oficio</label>
          <select v-model="form.user_id" class="form-control form-control-sm">
            <option :value="null" disabled>Seleccione un usuario...</option>
            <option v-for="user in oficioUsers(modalOficio?.id)" :key="user.id" :value="user.id">
              {{ user.name }}
            </option>
          </select>
          <p v-if="modalOficio && oficioUsers(modalOficio.id).length === 0" class="text-xxs text-danger mt-1 mb-0">
            No hay usuarios habilitados para este oficio.
          </p>
        </div>

        <!-- Productos (registros de la producción) que va a hacer el usuario -->
        <div class="mb-2 d-flex justify-content-between align-items-center">
          <label class="form-control-label text-xs mb-0">
            Productos que va a hacer <span class="text-secondary">(precio según el oficio en inventario)</span>
          </label>
          <span class="d-flex gap-2">
            <a class="text-xs cursor-pointer" @click="selectAllProducts">Todos</a>
            <a class="text-xs cursor-pointer text-danger" @click="clearProducts">Ninguno</a>
          </span>
        </div>
        <div class="product-check-list mb-2">
          <div
            v-for="p in assignableProducts"
            :key="p.id"
            class="product-check-row"
            :class="{ 'product-check-row--selected': isProductSelected(p.id) }"
          >
            <input
              type="checkbox"
              class="product-check"
              :checked="isProductSelected(p.id)"
              title="Incluir este producto en la asignación"
              @change="toggleProductSelection(p, $event.target.checked)"
            />
            <span class="flex-grow-1 min-width-0">
              <span class="text-xs font-weight-bold d-block text-truncate">
                {{ p.inventory?.name || 'Producto' }} <span class="text-xxs text-secondary">{{ p.inventory?.ref }}</span>
              </span>
              <span class="text-xxs text-secondary">{{ itemSize(p) }} / {{ itemColor(p) }} · Cant: {{ p.quantity }}</span>
            </span>
            <span v-if="isProductSelected(p.id)" class="product-inputs flex-shrink-0">
              <label class="product-input">
                <span class="text-xxs text-secondary">Cantidad</span>
                <input
                  v-model.number="productForm(p.id).quantity"
                  type="number"
                  min="1"
                  :max="p.quantity"
                  @click.stop
                />
              </label>
              <label class="product-input">
                <span class="text-xxs text-secondary">Precio</span>
                <input
                  v-model.number="productForm(p.id).price"
                  type="number"
                  min="0"
                  step="50"
                  @click.stop
                />
              </label>
            </span>
            <span v-else class="text-xxs text-end flex-shrink-0">
              ${{ recordOficioPrice(p).toLocaleString('es-CO') }} c/u
            </span>
          </div>
          <p v-if="assignableProducts.length === 0" class="text-xxs text-secondary mb-0 p-2">
            Todos los registros de esta producción ya están asignados para este oficio.
          </p>
        </div>

        <div class="alert alert-success py-2 px-3 mb-3 d-flex justify-content-between align-items-center">
          <span class="text-xs font-weight-bold mb-0">
            Total que gana {{ usuarioSeleccionado?.name || 'el usuario' }}
            <span class="text-xxs text-secondary d-block">
              {{ form.details.length }} producto(s) · {{ selectedPairs }} pares
            </span>
          </span>
          <span class="text-sm font-weight-bold">${{ assignmentTotal.toLocaleString('es-CO') }}</span>
        </div>

        <div class="row g-2 mb-3">
          <div class="col-6">
            <label class="form-control-label text-xs">Fecha de inicio (asignación)</label>
            <input v-model="form.assigned_at" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-6">
            <label class="form-control-label text-xs">Fecha de fin (plazo)</label>
            <input v-model="form.estimated_at" type="date" class="form-control form-control-sm" :min="form.assigned_at || undefined" />
            <p class="text-xxs text-secondary mt-1 mb-0">Hasta cuándo tiene plazo para terminar.</p>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-secondary mb-0" @click="closeAssignModal">Cancelar</button>
          <button
            type="button"
            class="btn btn-sm mb-0"
            style="background-color: #2e389b; border-color: #2e389b; color: #fff;"
            :disabled="saving"
            @click="saveAssignment"
          >
            <i class="fas" :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
            {{ editingId ? 'Guardar cambios' : 'Asignar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Marcar terminado (seguimiento por producto) -->
    <div v-if="showCompleteModal" class="custom-modal-backdrop" @click.self="closeCompleteModal">
      <div class="custom-modal custom-modal--wide">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">
            <i class="fas fa-check-circle me-1 text-success"></i>
            Terminados — {{ completeTarget?.oficio?.name }}
          </h6>
          <button type="button" class="btn btn-link p-0 text-secondary" @click="closeCompleteModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <p class="text-xs text-secondary mb-3">
          Usuario: <b>{{ completeTarget?.user?.name }}</b> · Inicio: {{ formatDate(completeTarget?.assigned_at) }}
        </p>

        <div v-if="modalError" class="alert alert-danger text-xs py-2 px-3 mb-3">{{ modalError }}</div>

        <!-- Con registros: indica cuántos pares terminó de cada producto -->
        <template v-if="completeTarget?.details && completeTarget.details.length > 0">
          <div class="product-check-list mb-2">
            <div
              v-for="asgDetail in completeTarget.details"
              :key="asgDetail.id"
              class="product-check-row"
              :class="{ 'product-check-row--selected': detailDelivered(asgDetail) >= asgDetail.quantity }"
            >
              <span class="flex-grow-1 min-width-0">
                <span class="text-xs font-weight-bold d-block text-truncate">{{ asgDetail.remission_detail?.inventory?.name || 'Producto' }}</span>
                <span class="text-xxs text-secondary">{{ detailVariantLabel(asgDetail) }} · Cant: {{ asgDetail.quantity }} × ${{ Number(asgDetail.price || 0).toLocaleString('es-CO') }}</span>
              </span>
              <span class="product-inputs flex-shrink-0">
                <label class="product-input">
                  <span class="text-xxs text-secondary">Cant. terminada</span>
                  <input
                    v-model.number="completeDetailEntry(asgDetail.id).delivered_quantity"
                    type="number"
                    min="0"
                    :max="asgDetail.quantity"
                    title="Cantidad terminada de este producto"
                  />
                </label>
                <span class="text-xxs text-end flex-shrink-0 align-self-center">
                  <span v-if="detailDelivered(asgDetail) >= asgDetail.quantity" class="badge badge-sm bg-success mb-1"><i class="fas fa-check"></i> Completo</span><br>
                  <b :class="detailDelivered(asgDetail) > 0 ? 'text-success' : 'text-muted'">
                    ${{ (detailDelivered(asgDetail) * Number(asgDetail.price || 0)).toLocaleString('es-CO') }}
                  </b>
                </span>
              </span>
            </div>
          </div>
          <div class="mb-3 d-flex justify-content-between align-items-center">
            <span class="text-xxs text-secondary">
              Indica cuántos pares terminó de cada producto; el resto queda pendiente.
            </span>
            <span class="text-xs">
              Gana: <b class="text-success">${{ completeEarned.toLocaleString('es-CO') }}</b> de ${{ Number(completeTarget.total || 0).toLocaleString('es-CO') }}
            </span>
          </div>
        </template>

        <!-- Asignaciones antiguas sin detalle por producto -->
        <div v-else class="mb-3">
          <label class="form-control-label text-xs">Cantidad de producto terminado</label>
          <input v-model.number="completeForm.delivered_quantity" type="number" min="0" class="form-control form-control-sm" placeholder="Cantidad terminada" />
        </div>

        <div class="mb-3">
          <label class="form-control-label text-xs">Fecha de terminar</label>
          <input v-model="completeForm.completed_at" type="date" class="form-control form-control-sm" :min="completeTarget?.assigned_at ? String(completeTarget.assigned_at).slice(0, 10) : undefined" />
        </div>

        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-secondary mb-0" @click="closeCompleteModal">Cancelar</button>
          <button
            type="button"
            class="btn btn-sm btn-success mb-0"
            :disabled="saving"
            @click="saveComplete"
          >
            <i class="fas" :class="saving ? 'fa-spinner fa-spin' : 'fa-check'"></i>
            Guardar seguimiento
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import oficiosService from '@/services/oficios.service';
import remissionsDetailsService from '@/services/remissionsDetails.service';
import productionOrdersService from '@/services/productionOrders.service';

export default {
  name: 'ProductionOrderDetails',
  data() {
    return {
      orderId: null,
      order: null,
      loading: true,
      statusUpdating: false,
      oficios: [],
      users: [],
      showAssignModal: false,
      modalOficio: null,
      editingId: null,
      modalError: '',
      saving: false,
      detailToggling: null,
      form: {
        user_id: null,
        assigned_at: new Date().toISOString().slice(0, 10),
        estimated_at: null,
        /** Productos seleccionados: [{ id, quantity, price }] */
        details: [],
      },
      showCompleteModal: false,
      completeTarget: null,
      completeForm: {
        completed_at: new Date().toISOString().slice(0, 10),
        /** Cantidades terminadas por producto: [{ id, delivered_quantity }] */
        details: [],
        delivered_quantity: null,
      },
    };
  },
  computed: {
    details() {
      return this.order?.details || [];
    },
    completedCount() {
      return this.details.filter((d) => d.manufacturing_status === 'completed').length;
    },
    /** Usuario actualmente seleccionado en el modal de asignación */
    usuarioSeleccionado() {
      return this.users.find((u) => u.id === this.form.user_id) || null;
    },
    /** Registros de la producción disponibles para el oficio del modal */
    assignableProducts() {
      if (!this.modalOficio) return [];
      const taken = this.takenDetailIdsFor(this.modalOficio.id, this.editingId);
      return this.details.filter((d) => !taken.has(String(d.id)));
    },
    /** Total que gana el usuario: suma de cantidad × precio de los productos seleccionados */
    assignmentTotal() {
      return this.form.details.reduce((sum, d) => sum + (parseInt(d.quantity) || 0) * (Number(d.price) || 0), 0);
    },
    /** Pares totales de los productos seleccionados en el modal */
    selectedPairs() {
      return this.form.details.reduce((sum, d) => sum + (parseInt(d.quantity) || 0), 0);
    },
    /** Total que gana el usuario según las cantidades terminadas indicadas */
    completeEarned() {
      if (!this.completeTarget) return 0;
      return (this.completeTarget.details || []).reduce((sum, d) => {
        const entry = this.completeForm.details.find((e) => e.id === d.id);
        const delivered = parseInt(entry?.delivered_quantity) || 0;
        return sum + delivered * (Number(d.price) || 0);
      }, 0);
    },
    /** Suma de la columna Cant de los productos de la producción */
    totalPairs() {
      return this.details.reduce((sum, d) => sum + (parseInt(d.quantity) || 0), 0);
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString('es-ES');
    },
    orderStatusPillClass(status) {
      if (status === 'fabricado') return 'status-pill--fabricado';
      if (status === 'terminado') return 'status-pill--terminado';
      return 'status-pill--in-progress';
    },
    /** Oficios (asignaciones) de la producción que siguen pendientes */
    pendingOficios() {
      return (this.order?.assignments || []).filter((a) => a.status !== 'completed');
    },
    async onOrderStatusChange(event) {
      const newStatus = event.target.value;
      const current = this.order.status || 'in_progress';
      const target = event.target;

      if (newStatus === current) return;

      // No se puede marcar como Terminado si hay oficios pendientes
      if (newStatus === 'terminado') {
        const pending = this.pendingOficios();
        if (pending.length > 0) {
          target.value = current;
          await Swal.fire({
            icon: 'warning',
            title: 'Cambio no permitido',
            html: `<p>No se puede marcar como Terminado: hay <b>${pending.length}</b> oficio(s) pendiente(s) por terminar.</p>
                   <p class="text-sm text-secondary mb-0">${pending
                     .map((a) => `${a.oficio?.name || 'Oficio'} — ${a.user?.name || 'Sin usuario'}`)
                     .join('<br>')}</p>`,
            confirmButtonColor: '#2e389b',
            customClass: { container: 'swal-high-z' },
          });
          return;
        }
        const result = await Swal.fire({
          title: '¿Confirmar cambio?',
          text: `¿Deseas marcar la producción #${this.orderId} como Terminado?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#2dce89',
          cancelButtonColor: '#6c757d',
          confirmButtonText: 'Sí, marcar como Terminado',
          cancelButtonText: 'Cancelar',
          customClass: { container: 'swal-high-z' },
        });
        if (!result.isConfirmed) {
          target.value = current;
          return;
        }
      }

      this.statusUpdating = true;
      try {
        await productionOrdersService.updateStatus(this.orderId, newStatus);
        this.order.status = newStatus;
      } catch (error) {
        console.error('Error al actualizar estado de la producción:', error);
        target.value = current;
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo actualizar el estado',
          customClass: { container: 'swal-high-z' },
        });
      } finally {
        this.statusUpdating = false;
      }
    },
    itemSize(item) {
      if (item.variant?.size?.size) return item.variant.size.size;
      return '—';
    },
    itemColor(item) {
      if (item.variant?.color?.name) return item.variant.color.name;
      return '—';
    },
    itemStock(item) {
      if (item.variant) return item.variant.quantity ?? 0;
      return item.inventory?.quantity ?? 0;
    },
    itemBarcode(item) {
      if (item.variant && item.variant.barcode) return item.variant.barcode;
      return item.inventory?.barcode || '—';
    },
    itemManufacturingStatus(item) {
      return item.manufacturing_status || 'pending';
    },
    statusPillClass(item) {
      const s = this.itemManufacturingStatus(item);
      if (s === 'completed') return 'status-pill--completed';
      if (s === 'in_progress') return 'status-pill--in-progress';
      return 'status-pill--pending';
    },
    async onStatusChange(event, item) {
      const newStatus = event.target.value;
      const current = this.itemManufacturingStatus(item);
      const target = event.target;

      if (newStatus === current) return;

      if (newStatus === 'completed') {
        const result = await Swal.fire({
          title: 'Confirmar fabricado',
          text: `¿Cambiar el estado de "${item.inventory?.name || 'Producto'}" a Fabricado? Se sumarán ${item.quantity} unidades al inventario.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, marcar fabricado',
          cancelButtonText: 'Cancelar',
          customClass: { container: 'swal-high-z' },
        });

        if (!result.isConfirmed) {
          target.value = current;
          return;
        }
      }

      try {
        const res = await remissionsDetailsService.updateManufacturingStatus(item.id, newStatus);
        Object.assign(item, { manufacturing_status: res.data.detail?.manufacturing_status || newStatus });
        await Swal.fire({
          icon: 'success',
          title: 'Estado actualizado',
          text: res.data.message,
          timer: 1500,
          showConfirmButton: false,
          customClass: { container: 'swal-high-z' },
        });
        if (res.data.remission_preparado_changed) {
          const rem = (this.order.remissions || []).find((r) => r.id === item.remission_id);
          if (rem) rem.preparado = res.data.remission_preparado;
        }
        // Recargar para reflejar stock y contadores actualizados
        await this.loadData();
      } catch (error) {
        console.error('Error al actualizar estado:', error);
        target.value = current;
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo actualizar el estado',
        });
      }
    },
    /** Asignaciones de la producción para un oficio */
    oficioAssignments(oficioId) {
      return (this.order?.assignments || []).filter((a) => String(a.oficio_id) === String(oficioId));
    },
    /** Usuarios habilitados para un oficio (campo processes del usuario) */
    oficioUsers(oficioId) {
      return this.users.filter((u) => this.userHasOficio(u, oficioId));
    },
    userHasOficio(user, oficioId) {
      let procs = user.processes;
      if (typeof procs === 'string') {
        try {
          procs = JSON.parse(procs);
        } catch (e) {
          procs = [];
        }
      }
      if (!Array.isArray(procs)) return false;
      return procs.map(String).includes(String(oficioId));
    },
    assignmentStatusLabel(asg) {
      return asg.status === 'completed' ? 'Terminado' : 'En proceso';
    },
    assignmentBadgeClass(asg) {
      return asg.status === 'completed' ? 'bg-success' : 'bg-warning text-dark';
    },
    openAssignModal(oficio, assignment = null) {
      this.modalOficio = oficio;
      this.editingId = assignment?.id || null;
      this.modalError = '';

      // Al asignar: todos los productos pendientes del oficio quedan seleccionados
      // con su cantidad y precio por defecto. Al editar: los que ya tiene la
      // asignación, con la cantidad y precio guardados.
      let productDetails;
      if (assignment) {
        productDetails = (assignment.details || []).map((d) => ({
          id: String(d.remission_detail_id),
          quantity: parseInt(d.quantity) || 0,
          price: Number(d.price) || 0,
        }));
      } else {
        const taken = this.takenDetailIdsFor(oficio.id, null);
        productDetails = this.details
          .filter((d) => !taken.has(String(d.id)))
          .map((d) => ({
            id: String(d.id),
            quantity: parseInt(d.quantity) || 0,
            price: this.recordOficioPrice(d),
          }));
      }

      const assignedAt = (assignment?.assigned_at || new Date().toISOString().slice(0, 10)).slice(0, 10);

      this.form = {
        user_id: assignment?.user_id ?? null,
        assigned_at: assignedAt,
        // Por defecto, mismo día de la asignación
        estimated_at: assignment?.estimated_at ? String(assignment.estimated_at).slice(0, 10) : assignedAt,
        details: productDetails,
      };
      this.showAssignModal = true;
    },
    closeAssignModal() {
      this.showAssignModal = false;
      this.modalOficio = null;
      this.editingId = null;
      this.modalError = '';
    },
    /** Indica si un producto está seleccionado en el modal de asignación */
    isProductSelected(detailId) {
      return this.form.details.some((d) => String(d.id) === String(detailId));
    },
    /** Entrada editable (cantidad/precio) de un producto seleccionado */
    productForm(detailId) {
      return this.form.details.find((d) => String(d.id) === String(detailId));
    },
    /** Marca o desmarca un producto, con cantidad y precio por defecto */
    toggleProductSelection(product, checked) {
      if (checked) {
        if (!this.isProductSelected(product.id)) {
          this.form.details.push({
            id: String(product.id),
            quantity: parseInt(product.quantity) || 0,
            price: this.recordOficioPrice(product),
          });
        }
      } else {
        this.removeProduct(product.id);
      }
    },
    /** Quita un producto de la asignación */
    removeProduct(detailId) {
      this.form.details = this.form.details.filter((d) => String(d.id) !== String(detailId));
    },
    selectAllProducts() {
      for (const p of this.assignableProducts) {
        this.toggleProductSelection(p, true);
      }
    },
    clearProducts() {
      this.form.details = [];
    },
    /** Ids de registros ya tomados por asignaciones de un oficio (como Set de strings) */
    takenDetailIdsFor(oficioId, exceptAssignmentId = null) {
      const ids = new Set();
      for (const asg of this.oficioAssignments(oficioId)) {
        if (exceptAssignmentId && asg.id === exceptAssignmentId) continue;
        for (const d of asg.details || []) ids.add(String(d.remission_detail_id));
      }
      return ids;
    },
    /** Registros de la producción pendientes para un oficio (sin asignar) */
    oficioPendingDetails(oficioId) {
      const taken = this.takenDetailIdsFor(oficioId, null);
      return this.details.filter((d) => !taken.has(String(d.id)));
    },
    /** Precio del oficio para un registro, según el inventario */
    recordOficioPrice(detail) {
      const oficios = detail?.inventory?.oficios || [];
      const match = oficios.find((o) => o.id === this.modalOficio?.id);
      return Number(match?.pivot?.price ?? 0) || 0;
    },
    validateForm() {
      if (!this.form.user_id) return 'Seleccione un usuario habilitado para este oficio.';
      if (!this.form.assigned_at) return 'Indique la fecha de inicio (asignación).';
      if (this.form.estimated_at && this.form.estimated_at < this.form.assigned_at) {
        return 'La fecha de fin (plazo) no puede ser anterior a la fecha de inicio.';
      }
      if (!this.form.details || this.form.details.length === 0) {
        return 'Seleccione al menos un producto que va a hacer el usuario.';
      }
      for (const d of this.form.details) {
        const p = this.details.find((x) => String(x.id) === String(d.id));
        const quantity = parseInt(d.quantity);
        if (!p) return 'Un producto seleccionado ya no pertenece a esta producción.';
        if (!quantity || quantity < 1 || quantity > parseInt(p.quantity)) {
          return `La cantidad de "${p.inventory?.name || 'Producto'}" debe estar entre 1 y ${p.quantity}.`;
        }
        const price = Number(d.price);
        if (Number.isNaN(price) || price < 0) {
          return `El precio de "${p.inventory?.name || 'Producto'}" no puede ser negativo.`;
        }
      }
      return null;
    },
    async saveAssignment() {
      const error = this.validateForm();
      if (error) {
        this.modalError = error;
        return;
      }
      this.saving = true;
      this.modalError = '';
      try {
        const payload = {
          user_id: this.form.user_id,
          assigned_at: this.form.assigned_at,
          estimated_at: this.form.estimated_at || null,
          details: this.form.details.map((d) => ({
            id: Number(d.id),
            quantity: parseInt(d.quantity),
            price: Number(d.price),
          })),
        };
        if (this.editingId) {
          await productionOrdersService.updateAssignment(this.editingId, payload);
        } else {
          await productionOrdersService.createAssignment(this.orderId, { ...payload, oficio_id: this.modalOficio.id });
        }
        this.closeAssignModal();
        await Swal.fire({
          icon: 'success',
          title: this.editingId ? 'Asignación actualizada' : 'Usuario asignado',
          text: this.editingId ? undefined : 'El oficio quedó en estado En proceso.',
          timer: 1400,
          showConfirmButton: false,
        });
        await this.loadData();
      } catch (error) {
        this.modalError = error.response?.data?.message || 'No se pudo guardar la asignación';
      } finally {
        this.saving = false;
      }
    },
    /** Pares terminados de una asignación (permite entregas parciales) */
    assignmentDeliveredPairs(asg) {
      return (asg.details || []).reduce((sum, d) => sum + (parseInt(d.delivered_quantity) || 0), 0);
    },
    /** Pares totales asignados de una asignación */
    assignmentTotalPairs(asg) {
      return (asg.details || []).reduce((sum, d) => sum + (parseInt(d.quantity) || 0), 0);
    },
    /** Total ganado según las cantidades terminadas de la asignación */
    assignmentEarned(asg) {
      return (asg.details || []).reduce(
        (sum, d) => sum + (parseInt(d.delivered_quantity) || 0) * (Number(d.price) || 0),
        0
      );
    },
    /** Etiqueta de talla/color del registro de asignación */
    detailVariantLabel(asgDetail) {
      const rd = asgDetail.remission_detail;
      if (!rd) return '';
      return `${this.itemSize(rd)} / ${this.itemColor(rd)}`;
    },
    /** Marca o desmarca un registro terminado desde la tarjeta */
    async toggleAssignmentDetail(asg, asgDetail) {
      const newStatus = asgDetail.status === 'completed' ? 'pending' : 'completed';
      this.detailToggling = asgDetail.id;
      try {
        const res = await productionOrdersService.updateAssignmentDetail(asgDetail.id, { status: newStatus });
        // Refrescar la asignación con los totales recalculados
        const updated = res.data.assignment;
        const index = (this.order?.assignments || []).findIndex((a) => a.id === updated.id);
        if (index >= 0) this.order.assignments.splice(index, 1, updated);
      } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'No se pudo actualizar el registro', 'error');
      } finally {
        this.detailToggling = null;
      }
    },
    openCompleteModal(assignment) {
      this.completeTarget = assignment;
      this.modalError = '';
      // Cantidades terminadas por producto: por defecto, la cantidad total del
      // producto; si ya tenía una entrega guardada, se conserva ese valor
      this.completeForm = {
        completed_at: new Date().toISOString().slice(0, 10),
        details: (assignment.details || []).map((d) => {
          const saved = parseInt(d.delivered_quantity) || 0;
          return {
            id: d.id,
            delivered_quantity: saved > 0 ? saved : parseInt(d.quantity) || 0,
          };
        }),
        delivered_quantity: assignment.quantity ?? 0,
      };
      this.showCompleteModal = true;
    },
    closeCompleteModal() {
      this.showCompleteModal = false;
      this.completeTarget = null;
      this.modalError = '';
    },
    /** Entrada de cantidad terminada de un producto del modal de seguimiento */
    completeDetailEntry(detailId) {
      let entry = this.completeForm.details.find((d) => d.id === detailId);
      if (!entry) {
        entry = { id: detailId, delivered_quantity: 0 };
        this.completeForm.details.push(entry);
      }
      return entry;
    },
    /** Cantidad terminada indicada para un producto */
    detailDelivered(asgDetail) {
      const entry = this.completeForm.details.find((d) => d.id === asgDetail.id);
      return parseInt(entry?.delivered_quantity) || 0;
    },
    async saveComplete() {
      if (!this.completeForm.completed_at) {
        this.modalError = 'Indique la fecha de terminar.';
        return;
      }
      const hasDetails = this.completeTarget?.details && this.completeTarget.details.length > 0;
      if (!hasDetails && (this.completeForm.delivered_quantity === null || this.completeForm.delivered_quantity === '' || this.completeForm.delivered_quantity < 0)) {
        this.modalError = 'Indique la cantidad de producto terminado.';
        return;
      }
      if (hasDetails) {
        for (const d of this.completeTarget.details) {
          const delivered = this.detailDelivered(d);
          if (delivered < 0 || delivered > parseInt(d.quantity)) {
            const name = d.remission_detail?.inventory?.name || 'Producto';
            this.modalError = `La cantidad terminada de "${name}" debe estar entre 0 y ${d.quantity}.`;
            return;
          }
        }
      }
      const inicio = this.completeTarget?.assigned_at ? String(this.completeTarget.assigned_at).slice(0, 10) : null;
      if (inicio && this.completeForm.completed_at < inicio) {
        this.modalError = 'La fecha de terminar no puede ser anterior a la fecha de inicio.';
        return;
      }
      this.saving = true;
      this.modalError = '';
      try {
        const payload = {
          completed_at: this.completeForm.completed_at,
          ...(hasDetails
            ? { details: this.completeForm.details.map((d) => ({ id: d.id, delivered_quantity: parseInt(d.delivered_quantity) || 0 })) }
            : { delivered_quantity: this.completeForm.delivered_quantity }),
        };
        await productionOrdersService.completeAssignment(this.completeTarget.id, payload);
        this.closeCompleteModal();
        await Swal.fire({
          icon: 'success',
          title: 'Seguimiento actualizado',
          timer: 1400,
          showConfirmButton: false,
        });
        await this.loadData();
      } catch (error) {
        this.modalError = error.response?.data?.message || 'No se pudo guardar el seguimiento';
      } finally {
        this.saving = false;
      }
    },
    async deleteAssignment(assignment) {
      const result = await Swal.fire({
        title: 'Eliminar asignación',
        text: `¿Eliminar la asignación de ${assignment.user?.name || 'este usuario'}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e91e63',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      });
      if (!result.isConfirmed) return;
      try {
        await productionOrdersService.deleteAssignment(assignment.id);
        await this.loadData();
      } catch (error) {
        await Swal.fire('Error', error.response?.data?.message || 'No se pudo eliminar la asignación', 'error');
      }
    },
    async loadCatalogs() {
      try {
        const [usersRes, oficiosRes] = await Promise.all([
          this.$store.dispatch('users/getAll', 'all'),
          oficiosService.getOficios(),
        ]);
        this.users = usersRes || [];
        this.oficios = oficiosRes.data || [];
      } catch (error) {
        console.error('Error al cargar usuarios/oficios:', error);
      }
    },
    async loadData() {
      this.orderId = this.$route.query.id;
      if (!this.orderId) {
        await Swal.fire('Error', 'Producción no especificada', 'error');
        return;
      }
      this.loading = true;
      try {
        const res = await productionOrdersService.getData(this.orderId);
        this.order = res.data;
      } catch (error) {
        console.error('Error al cargar la producción:', error);
        await Swal.fire('Error', error.response?.data?.message || 'No se pudo cargar la producción', 'error');
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    await Promise.all([this.loadData(), this.loadCatalogs()]);
  },
  watch: {
    '$route.query.id'(newId) {
      // Al navegar fuera (p. ej. de /produccion/detalles a /produccion) la ruta
      // global cambia antes de desmontar este componente; ignorar ese cambio.
      if (this.$route.name !== 'Production Order Details') return;
      if (!newId) return;
      this.loadData();
    },
  },
};
</script>

<style scoped>
.oficio-card {
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
}

.oficio-users li {
  background-color: #f8f9fa;
}

.custom-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2050;
}

.custom-modal {
  background: #fff;
  border-radius: 0.75rem;
  padding: 1.25rem;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.custom-modal--wide {
  max-width: 640px;
  max-height: 90vh;
  overflow-y: auto;
}

/* Lista de productos con checkbox (asignación y seguimiento) */
.product-check-list {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  max-height: 260px;
  overflow-y: auto;
}

.product-check-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid #f1f3f5;
  cursor: pointer;
  margin: 0;
}

.product-check-row:last-child {
  border-bottom: none;
}

.product-check-row:hover {
  background-color: #f8f9fa;
}

.product-check-row--selected {
  background-color: #f2f4ff;
}

.product-check-row .product-check {
  width: 15px;
  height: 15px;
  margin: 0;
  flex-shrink: 0;
  cursor: pointer;
  appearance: auto;
  -webkit-appearance: checkbox;
}

/* Inputs de cantidad/precio por producto (asignación y seguimiento) */
.product-inputs {
  display: flex;
  align-items: flex-end;
  gap: 0.4rem;
}

.product-input {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  margin: 0;
}

.product-input input {
  width: 4.6rem;
  padding: 0.15rem 0.35rem;
  font-size: 0.72rem;
  border: 1px solid #dee2e6;
  border-radius: 0.35rem;
  outline: none;
}

.product-input input:focus {
  border-color: #2e389b;
  box-shadow: 0 0 0 2px rgba(46, 56, 155, 0.12);
}

/* Ocultar las flechas de los inputs numéricos */
.product-input input::-webkit-outer-spin-button,
.product-input input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.product-input input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.min-width-0 {
  min-width: 0;
}

.assignment-products li {
  border-bottom: 1px dashed #e9ecef;
}

/* Acciones de la asignación: editar/eliminar pegadas a la derecha y centradas.
   El margin-bottom lo agrega el tema global a todos los .btn */
.assignment-actions .btn.btn-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  margin-bottom: 0;
}

.assignment-products li:last-child {
  border-bottom: none;
}

/* Select de estado (igual que picking-packing) */
.status-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.3rem 1.8rem 0.3rem 0.8rem;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1.2;
  outline: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='currentColor' viewBox='0 0 16 16'%3e%3cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.55rem center;
  background-size: 10px;
}

.status-select:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.status-select:focus {
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.status-select option {
  font-size: 0.85rem;
  padding: 0.3rem 0.5rem;
  border-radius: 0;
}

.status-pill--pending {
  background: #f3f4f6;
  color: #6b7280;
}

.status-pill--pending:hover {
  background: #e5e7eb;
}

.status-pill--in-progress {
  background: #fef3c7;
  color: #b45309;
}

.status-pill--in-progress:hover {
  background: #fde68a;
}

.status-pill--completed {
  background: #d1fae5;
  color: #047857;
}

.status-pill--completed:hover {
  background: #a7f3d0;
}

/* Estados de la orden de producción */
.status-select.status-pill--fabricado {
  background-color: #cce5ff;
  color: #004085;
}

.status-select.status-pill--terminado {
  background-color: #d4edda;
  color: #155724;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
}

.status-badge--completed {
  background: #d1fae5;
  color: #047857;
}
</style>
